import { ProductFormFields } from "@/features/events/features/products/components/ProductFormFields";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useFinalizeTemplateWizard } from "@/features/templates/hooks/useFinalizeTemplateWizard";
import { useTemplatesCopy } from "@/features/i18n/templatesCopy";
import { templateSettingsSchema } from "@patient-watch/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { ui } from "@patient-watch/ui";

type ProductDetailsValues = Pick<
    z.infer<typeof templateSettingsSchema>,
    "orthopaedic_area" | "product_option_id" | "usage_instructions" | "provided_by"
>;

const ProductDetailsPage = () => {
    const templatesCopy = useTemplatesCopy();
    const { templateId } = Route.useParams();
    const search = Route.useSearch();
    const navigate = useNavigate();
    const { markStepComplete, nextStep, setCurrentStep, getNextStepRoute, getPreviousStepRoute, currentStep } =
        useTemplateNavigationStore();
    const { finalize, isSubmitting, isTemplateOwnerLookupBlockingSubmit } = useFinalizeTemplateWizard(
        templateId,
        search
    );

    useEffect(() => {
        setCurrentStep("product");
    }, [setCurrentStep]);

    const form = useForm<ProductDetailsValues>({
        resolver: zodResolver(
            templateSettingsSchema.pick({
                orthopaedic_area: true,
                product_option_id: true,
                usage_instructions: true,
                provided_by: true,
            })
        ),
        defaultValues: {
            orthopaedic_area: search.orthopaedic_area ?? undefined,
            product_option_id: search.product_option_id ?? undefined,
            usage_instructions: search.usage_instructions ?? undefined,
            provided_by: search.provided_by ?? undefined,
        },
    });

    const handleNext = (data: ProductDetailsValues) => {
        markStepComplete(currentStep);
        const nextRoute = getNextStepRoute(currentStep, { templateId });
        if (nextRoute) {
            nextStep();
            navigate({
                ...nextRoute,
                search: {
                    ...search,
                    ...data,
                },
            });
        } else {
            finalize(data);
        }
    };

    const handlePrevious = () => {
        const prevRoute = getPreviousStepRoute(currentStep, { templateId });
        if (prevRoute) {
            navigate({
                ...prevRoute,
                search: {
                    ...search,
                    ...form.getValues(),
                },
            });
        }
    };

    return (
        <ui.Form {...form}>
            <form onSubmit={form.handleSubmit(handleNext)} className="space-y-6">
                <ProductFormFields
                    orthopaedicAreaFieldName="orthopaedic_area"
                    productOptionIdFieldName="product_option_id"
                />
                <TemplateStepFooter
                    onBack={handlePrevious}
                    submitPrimary
                    primaryAction="save"
                    primaryLabel={templatesCopy.saveTemplate}
                    isLoadingPrimary={isSubmitting}
                    disabledPrimary={isTemplateOwnerLookupBlockingSubmit}
                />
            </form>
        </ui.Form>
    );
};

const productSearchSchema = templateSettingsSchema.partial().pick({
    orthopaedic_area: true,
    product_option_id: true,
    usage_instructions: true,
    provided_by: true,
    required_events: true,
});

export const Route = createFileRoute("/_app/templates/$templateId/events/product/")({
    component: ProductDetailsPage,
    validateSearch: (search) => productSearchSchema.parse(search),
});
